import type { ApiFailure, ApiResponse } from '@osce/contracts'

/**
 * Klien API terpusat — satu-satunya tempat `fetch` dipanggil.
 *
 * Token akses disimpan di memori, bukan localStorage: umur prosesnya sama
 * dengan tab, dan tidak dapat dibaca skrip lain lewat storage.
 *
 * Refresh token pun di memori, sehingga rotasi senyap hanya berlaku **selama
 * tab hidup**: permintaan yang kena 401 diperbarui tanpa mengganggu pengguna.
 * Muat-ulang halaman tetap kembali ke layar masuk. Persistensi lintas
 * muat-ulang menunggu keputusan penyimpanan yang ditinjau keamanan — cookie
 * httpOnly di sisi proxy. Lihat `AuthProvider`.
 */

export class ApiError extends Error {
  readonly code: string
  readonly details: ApiFailure['error']['details']
  readonly status: number

  constructor(status: number, failure: ApiFailure['error']) {
    super(failure.message)
    this.name = 'ApiError'
    this.status = status
    this.code = failure.code
    this.details = failure.details
  }
}

let accessToken: string | null = null
let refreshTokenValue: string | null = null

export function setTokens(access: string | null, refresh: string | null): void {
  accessToken = access
  refreshTokenValue = refresh
}

export function getRefreshToken(): string | null {
  return refreshTokenValue
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  readonly body?: unknown
  /** Lewati percobaan refresh saat 401; dipakai endpoint auth sendiri. */
  readonly skipRefresh?: boolean
}

interface Enveloped<T> {
  readonly data: T
  readonly meta?: { page: number; limit: number; total: number }
}

async function rawRequest<T>(path: string, options: RequestOptions): Promise<Enveloped<T>> {
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers: {
      'content-type': 'application/json',
      ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  })

  let parsed: ApiResponse<T> & { meta?: Enveloped<T>['meta'] }
  try {
    parsed = (await response.json()) as ApiResponse<T> & { meta?: Enveloped<T>['meta'] }
  } catch {
    throw new ApiError(response.status, {
      code: 'INTERNAL_ERROR',
      message: 'Respons server tidak dapat dibaca',
    })
  }

  if (!parsed.success) throw new ApiError(response.status, parsed.error)
  return parsed.meta === undefined
    ? { data: parsed.data }
    : { data: parsed.data, meta: parsed.meta }
}

/** Berjalan saat sesi berakhir dan tidak dapat dipulihkan. Dipasang oleh AuthProvider. */
let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler
}

/** Satu refresh berjalan pada satu waktu; permintaan lain menunggu hasilnya. */
let refreshing: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshTokenValue === null) return false
  refreshing ??= (async () => {
    try {
      const { data } = await rawRequest<{ accessToken: string; refreshToken: string }>(
        '/api/v1/auth/refresh',
        { method: 'POST', body: { refreshToken: refreshTokenValue }, skipRefresh: true },
      )
      setTokens(data.accessToken, data.refreshToken)
      return true
    } catch {
      setTokens(null, null)
      return false
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

async function requestEnveloped<T>(path: string, options: RequestOptions): Promise<Enveloped<T>> {
  try {
    return await rawRequest<T>(path, options)
  } catch (error) {
    const isAuthFailure =
      error instanceof ApiError && error.status === 401 && options.skipRefresh !== true

    if (!isAuthFailure) throw error

    if (await tryRefresh()) {
      return rawRequest<T>(path, options)
    }

    onSessionExpired?.()
    throw error
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await requestEnveloped<T>(path, options)).data
}

/** Untuk endpoint daftar: mengembalikan data beserta meta paginasi. */
export async function requestWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; total: number }> {
  const { data, meta } = await requestEnveloped<T>(path, options)
  return { data, total: meta?.total ?? 0 }
}
